/**
 * contract-helpers.ts — Kiểu dữ liệu + hàm thuần cho Platform → Hợp đồng (ký tay online) và trang ký công khai.
 * Backend: ecdp_backend_source/src/modules/platform/contract/* (server kiểm lại mọi thứ — ở đây chỉ kiểm sớm để báo lỗi nhanh).
 */

export type ContractStatus = 'draft' | 'sent' | 'signed' | 'declined' | 'expired' | 'cancelled'
export const STATUS_LABELS: Record<ContractStatus, string> = {
  draft: 'Nháp', sent: 'Đã gửi — chờ ký', signed: 'Đã ký', declined: 'Khách từ chối', expired: 'Quá hạn ký', cancelled: 'Đã huỷ',
}
export const STATUS_VARIANT: Record<ContractStatus, 'secondary' | 'default' | 'success' | 'outline' | 'warning' | 'destructive'> = {
  draft: 'secondary', sent: 'default', signed: 'success', declined: 'destructive', expired: 'warning', cancelled: 'outline',
}

export interface Clause { title: string; text: string }

/** Bố cục từng hợp đồng (khớp backend contract-calc.ts). `partiesInText` = toàn văn: khối các bên nằm trong điều khoản. */
export type PartyRole = 'A' | 'B'
export interface ContractLayout { title: string; sellerRole: PartyRole; partiesInText: boolean; sellerDisplay: string }
export const DEFAULT_LAYOUT: ContractLayout = { title: 'HỢP ĐỒNG DỊCH VỤ', sellerRole: 'A', partiesInText: false, sellerDisplay: '' }
export const otherRole = (r: PartyRole): PartyRole => (r === 'A' ? 'B' : 'A')
export interface ContractCustomer { fullName: string; idNumber: string; address: string; email: string; phone: string }
export interface ContractDeal { description: string; amount: number | null }

export interface ContractListItem {
  id: string
  number: string
  customerName: string
  customerEmail: string
  amount: number | null
  description: string
  status: ContractStatus
  displayStatus: ContractStatus
  expiresAt: string | null
  sentAt: string | null
  signedAt: string | null
  createdAt: string
}

export interface ContractEvent {
  id: string
  type: string
  actor: string
  ip: string | null
  userAgent: string | null
  meta: Record<string, unknown>
  at: string
}

export interface Contract {
  id: string
  number: string
  orgId: string | null
  quoteId: string | null
  customer: ContractCustomer
  deal: ContractDeal
  seller: { companyName?: string; signerName?: string; signerTitle?: string }
  layout: ContractLayout
  hasSellerSignature: boolean
  body: Clause[]
  bodyHash: string | null
  status: ContractStatus
  displayStatus: ContractStatus
  expiresAt: string | null
  sentAt: string | null
  note: string
  signerName: string | null
  signedAt: string | null
  signedIp: string | null
  signedUserAgent: string | null
  declinedAt: string | null
  declineReason: string | null
  cancelledAt: string | null
  cancelReason: string | null
  hasPdf: boolean
  pdfHash: string | null
  pdfBytes: number | null
  createdByName?: string | null
  createdAt: string
  events?: ContractEvent[]
}

export interface ContractTemplate {
  companyName: string
  taxCode: string
  address: string
  phone: string
  email: string
  signerName: string
  signerTitle: string
  hasSignature: boolean
  expiresDays: number
  clauses: Clause[]
}

export interface TemplateResponse {
  template: ContractTemplate
  signaturePreview: string | null
  variables: Array<{ key: string; label: string }>
  updatedAt: string | null
}

export const EVENT_LABELS: Record<string, string> = {
  created: 'Lập hợp đồng', updated: 'Sửa hợp đồng', sent: 'Gửi link ký cho khách', email_failed: 'Gửi email lỗi',
  viewed: 'Khách mở link', otp_requested: 'Khách xin mã OTP', otp_throttled: 'Xin mã OTP bị chặn (quá nhanh / quá nhiều)',
  otp_failed: 'Nhập sai mã OTP', otp_locked: 'Khoá nhập OTP (sai quá 5 lần)', sign_pdf_failed: 'Dựng PDF lỗi — chưa ký',
  integrity_failed: 'Nội dung không khớp mã băm — chặn ký', signed: 'Khách đã ký', signed_email: 'Gửi bản đã ký cho khách',
  declined: 'Khách từ chối', cancelled: 'Huỷ hợp đồng', downloaded: 'Tải PDF đã ký', copied: 'Đã sao chép thành hợp đồng mới',
}
/** Sự kiện cần chú ý (nghi ngờ / lỗi) — tô màu cảnh báo trong nhật ký. */
export const EVENT_WARN = new Set(['email_failed', 'otp_throttled', 'otp_failed', 'otp_locked', 'sign_pdf_failed', 'integrity_failed'])

// ── Định dạng ────────────────────────────────────────────────────────

export const fmtMoney = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${new Intl.NumberFormat('vi-VN').format(n)} ₫`)

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/** Còn bao lâu tới hạn: "còn 3 ngày", "còn 5 giờ", "đã quá hạn". */
export function timeLeft(iso: string | null, now: Date = new Date()): string {
  if (!iso) return ''
  const ms = new Date(iso).getTime() - now.getTime()
  if (Number.isNaN(ms)) return ''
  if (ms <= 0) return 'đã quá hạn'
  const hours = Math.floor(ms / 3_600_000)
  if (hours >= 24) return `còn ${Math.floor(hours / 24)} ngày`
  if (hours >= 1) return `còn ${hours} giờ`
  return `còn ${Math.max(1, Math.floor(ms / 60_000))} phút`
}

// ── Form ─────────────────────────────────────────────────────────────

export interface ContractForm {
  fullName: string
  idNumber: string
  address: string
  email: string
  phone: string
  description: string
  amount: string
  note: string
  orgId: string | null
  quoteId: string | null
  /** null = dùng điều khoản của Mẫu hợp đồng. */
  clauses: Clause[] | null
  layout: ContractLayout
  /** id hợp đồng nguồn khi lập bằng "Sao chép" (server ghi dấu vết hai chiều). */
  copiedFrom: string | null
}

export const emptyForm = (): ContractForm => ({
  fullName: '', idNumber: '', address: '', email: '', phone: '', description: '', amount: '', note: '', orgId: null, quoteId: null, clauses: null,
  layout: { ...DEFAULT_LAYOUT }, copiedFrom: null,
})

export function contractToForm(c: Contract): ContractForm {
  return {
    fullName: c.customer.fullName, idNumber: c.customer.idNumber, address: c.customer.address, email: c.customer.email,
    phone: c.customer.phone, description: c.deal.description, amount: c.deal.amount === null ? '' : String(c.deal.amount),
    note: c.note, orgId: c.orgId, quoteId: c.quoteId, clauses: c.body, layout: { ...DEFAULT_LAYOUT, ...c.layout }, copiedFrom: null,
  }
}

/** Hợp đồng nào được sao chép: đã huỷ / khách từ chối / quá hạn ký / đã ký (nháp thì sửa, đang chờ ký thì huỷ trước). */
export const canCopyContract = (displayStatus: ContractStatus) => ['cancelled', 'declined', 'expired', 'signed'].includes(displayStatus)

/** Form lập mới điền sẵn từ một hợp đồng đã gửi: khách, dịch vụ, ĐIỀU KHOẢN (đã thay biến), bố cục, báo giá; số hợp đồng do hệ thống cấp mới. */
export function copyToForm(c: Contract): ContractForm {
  const f = contractToForm(c)
  const tag = `Sao chép từ ${c.number}`
  return { ...f, note: f.note ? `${tag}\n${f.note}`.slice(0, 500) : tag, copiedFrom: c.id }
}

const EMAIL_RE = /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']{2,}$/

/** Lỗi nhập (tiếng Việt) hoặc null. */
export function validateForm(f: ContractForm): string | null {
  if (f.fullName.trim().length < 2) return 'Nhập họ tên khách'
  if (!EMAIL_RE.test(f.email.trim())) return 'Email khách không hợp lệ (mã OTP sẽ gửi về email này)'
  if (f.idNumber.trim() && !/^[0-9A-Za-z]{6,20}$/.test(f.idNumber.trim())) return 'Số CCCD/CMND chỉ gồm chữ và số (6–20 ký tự)'
  if (f.phone.trim() && !/^[0-9+() .-]{8,20}$/.test(f.phone.trim())) return 'SĐT khách không hợp lệ'
  if (f.amount.trim() && !/^\d{1,13}$/.test(f.amount.trim())) return 'Số tiền phải là số nguyên đồng'
  if (!f.layout.title.trim()) return 'Nhập tiêu đề hợp đồng'
  if (f.clauses) {
    if (f.clauses.length === 0) return 'Cần ít nhất 1 điều khoản'
    const bad = f.clauses.findIndex((c) => !c.title.trim() || !c.text.trim())
    if (bad >= 0) return `Điều khoản ${bad + 1}: nhập đủ tiêu đề và nội dung`
  }
  return null
}

export function formToInput(f: ContractForm) {
  return {
    customer: { fullName: f.fullName.trim(), idNumber: f.idNumber.trim(), address: f.address.trim(), email: f.email.trim(), phone: f.phone.trim() },
    deal: { description: f.description.trim(), amount: f.amount.trim() === '' ? null : Number(f.amount.trim()) },
    clauses: f.clauses,
    layout: { ...f.layout, title: f.layout.title.trim(), sellerDisplay: f.layout.sellerDisplay.trim() },
    copiedFrom: f.copiedFrom,
    note: f.note.trim(),
    orgId: f.orgId,
    quoteId: f.quoteId,
  }
}

/** Chèn `{{biến}}` vào cuối nội dung điều khoản. */
export const insertVariable = (text: string, key: string) => `${text}${text && !/\s$/.test(text) ? ' ' : ''}{{${key}}}`

export function moveClause(list: Clause[], from: number, to: number): Clause[] {
  if (to < 0 || to >= list.length || from === to) return list
  const next = [...list]
  const [it] = next.splice(from, 1)
  next.splice(to, 0, it)
  return next
}

// ── Trang ký công khai ───────────────────────────────────────────────

export interface PublicContract {
  number: string
  status: ContractStatus
  statusLabel: string
  seller: { companyName: string; signerName: string; signerTitle: string }
  layout: { title: string; sellerRole: PartyRole; partiesInText: boolean }
  customer: { fullName: string; emailMasked: string }
  clauses: Clause[]
  expiresAt: string | null
  signedAt: string | null
  canSign: boolean
  hasSellerSignature: boolean
}

/** Mã OTP: chỉ giữ chữ số, tối đa 6 (dán "123 456" vẫn nhận). */
export const normalizeOtp = (s: string) => s.replace(/\D/g, '').slice(0, 6)

export interface SignCheck { name: string; otp: string; hasStroke: boolean; agree: boolean; otpSent: boolean }
/** Lỗi trước khi bấm Ký, hoặc null. */
export function signBlocker(c: SignCheck): string | null {
  if (c.name.trim().length < 2) return 'Nhập họ tên đầy đủ'
  if (!c.hasStroke) return 'Hãy vẽ chữ ký vào khung'
  if (!c.otpSent) return 'Bấm "Gửi mã xác thực" để nhận mã qua email'
  if (c.otp.length !== 6) return 'Nhập mã xác thực gồm 6 chữ số'
  if (!c.agree) return 'Tick đồng ý với nội dung hợp đồng'
  return null
}

/** Đếm ngược cho nút "Gửi lại mã": giây còn lại (≥0). */
export const cooldownLeft = (sentAtMs: number | null, nowMs: number, cooldownSec = 60) =>
  sentAtMs === null ? 0 : Math.max(0, Math.ceil((sentAtMs + cooldownSec * 1000 - nowMs) / 1000))

/** Từ <canvas> → có nét vẽ không (đủ số điểm ảnh không trong suốt). Nhận ImageData để test được không cần DOM. */
export function hasInk(data: Uint8ClampedArray, minPixels = 40): boolean {
  let n = 0
  for (let i = 3; i < data.length; i += 4) if (data[i] > 20 && ++n >= minPixels) return true
  return false
}

// ── Nhập hợp đồng toàn văn từ văn bản dán vào ────────────────────────

export interface ImportedContract { title: string | null; clauses: Clause[]; warnings: string[] }

// "Điều 1. Tên" / "Phụ lục 01. Tên" — KHÔNG khớp câu thường như "Điều 16.2 hoặc…" hay "Phụ lục 01, 02, 03 là…".
const HEADING_RE = /^(?:Điều\s+\d+\s*[.:]\s+\S.*|Phụ\s+lục\s+\d+\s*(?:[.:–—-]\s+\S.*)?)$/iu
const NATIONAL_RE = /^(CỘNG\s+H[ÒOÓ]A\s+XÃ\s+HỘI\s+CHỦ\s+NGHĨA\s+VIỆT\s+NAM|Độc\s+lập\s*[–—-]\s*Tự\s+do\s*[–—-]\s*Hạnh\s+phúc)\s*$/iu
/** Dòng thừa cuối điều khoản do khối chữ ký của file gốc (BÊN A / BÊN B / (Ký, ghi rõ họ tên) / dòng chấm / <br>). */
const SIGN_JUNK_RE = /^(BÊN\s+[AB]|\(?Ký,?\s+ghi\s+rõ\s+họ\s+tên\)?|\.{4,}|(<br\s*\/?>\s*)+)$/iu
const MAX_TITLE = 200
const MAX_TEXT = 4000
const MAX_CLAUSES = 40

const joinLines = (lines: string[]) => lines.join('\n').replace(/\n{3,}/g, '\n\n').trim()

/**
 * Tách văn bản hợp đồng (dán từ Word) thành: tiêu đề + danh sách điều khoản.
 *  - Bỏ quốc hiệu / tiêu ngữ / dòng "Số:" (hệ thống tự in); dòng IN HOA bắt đầu "HỢP ĐỒNG" = tiêu đề.
 *  - Phần mở đầu (Căn cứ…, các bên) → 1–2 điều khoản; mỗi "Điều N." / "Phụ lục N." → một điều khoản.
 *  - Bỏ dòng thừa của khối chữ ký ở cuối điều khoản.
 * Hàm thuần — backend vẫn kiểm lại mọi thứ.
 */
/**
 * Chuẩn hoá văn bản dán từ Word: "•⇥nội dung" → "• nội dung"; các dòng liên tiếp có TAB (bảng Word dán ra) → bảng `| a | b |`
 * (dòng đầu là hàng tiêu đề). Dòng đã là bảng `|…|` giữ nguyên.
 */
export function normalizePastedText(raw: string): string[] {
  const src = raw.replace(/\r\n?/g, '\n').split('\n').map((l) => l.replace(/\s+$/g, '').replace(/^[•·▪‣◦o]\t+/u, '• '))
  const out: string[] = []
  const cells = (l: string) => l.split('\t').map((c) => c.trim().replace(/\|/g, '\\|'))
  const isTsv = (l: string) => !l.trimStart().startsWith('|') && l.split('\t').filter((c) => c.trim() !== '').length >= 2
  for (let i = 0; i < src.length; i++) {
    if (isTsv(src[i])) {
      let j = i
      while (j < src.length && isTsv(src[j])) j++
      if (j - i >= 2) {
        const rows = src.slice(i, j).map(cells)
        const n = Math.max(...rows.map((r) => r.length))
        const pad = (r: string[]) => Array.from({ length: n }, (_, k) => r[k] ?? '')
        out.push(`| ${pad(rows[0]).join(' | ')} |`, `|${'---|'.repeat(n)}`, ...rows.slice(1).map((r) => `| ${pad(r).join(' | ')} |`))
        i = j - 1
        continue
      }
    }
    out.push(src[i])
  }
  return out
}

export function parseContractText(raw: string): ImportedContract {
  const warnings: string[] = []
  const lines = normalizePastedText(raw)
  const firstHeading = lines.findIndex((l) => HEADING_RE.test(l.trim()))
  if (firstHeading < 0) return { title: null, clauses: [], warnings: ['Không thấy dòng "Điều 1." hay "Phụ lục 01." nào — kiểm tra lại nội dung đã dán'] }

  // Phần mở đầu
  let title: string | null = null
  const pre: string[] = []
  for (const l of lines.slice(0, firstHeading)) {
    const t = l.trim()
    if (!t || NATIONAL_RE.test(t) || /^Số\s*:/iu.test(t)) continue
    if (title === null && /^HỢP\s+ĐỒNG\b/u.test(t) && t === t.toUpperCase()) { title = t; continue }
    pre.push(t)
  }
  const clauses: Clause[] = []
  const split = pre.findIndex((l) => /^(Hôm\s+nay|Chúng\s+tôi\s+gồm)/iu.test(l))
  const groundsPart = split < 0 ? pre : pre.slice(0, split)
  const partiesPart = split < 0 ? [] : pre.slice(split)
  if (groundsPart.length) {
    const head = groundsPart[0]
    if (groundsPart.length > 1 && head.length <= 60) clauses.push({ title: head.replace(/[:：]\s*$/, ''), text: joinLines(groundsPart.slice(1)) })
    else clauses.push({ title: 'Mở đầu', text: joinLines(groundsPart) })
  }
  if (partiesPart.length) clauses.push({ title: 'Các bên ký kết', text: joinLines(partiesPart) })

  // Điều / Phụ lục
  let cur: { title: string; body: string[] } | null = null
  const flush = () => {
    if (!cur) return
    const body = [...cur.body]
    while (body.length && (!body[body.length - 1].trim() || SIGN_JUNK_RE.test(body[body.length - 1].trim()))) body.pop()
    clauses.push({ title: cur.title, text: joinLines(body) || '(Không có nội dung)' })
    cur = null
  }
  for (const l of lines.slice(firstHeading)) {
    const t = l.trim()
    if (HEADING_RE.test(t)) { flush(); cur = { title: t, body: [] } } else if (cur) cur.body.push(t)
  }
  flush()

  clauses.forEach((c, i) => {
    if (c.title.length > MAX_TITLE) warnings.push(`Mục ${i + 1}: tiêu đề dài quá ${MAX_TITLE} ký tự`)
    if (c.text.length > MAX_TEXT) warnings.push(`"${c.title.slice(0, 40)}": nội dung ${c.text.length} ký tự, vượt giới hạn ${MAX_TEXT} — hãy tách thành hai điều khoản`)
  })
  if (clauses.length > MAX_CLAUSES) warnings.push(`Có ${clauses.length} điều khoản, tối đa ${MAX_CLAUSES}`)
  return { title, clauses, warnings }
}

/** Số chỗ "......" (≥4 dấu chấm liền) còn để trống trong điều khoản — nhắc điền trước khi gửi. */
export function countBlanks(clauses: Clause[]): number {
  return clauses.reduce((n, c) => n + ((`${c.title}\n${c.text}`).match(/\.{4,}/g)?.length ?? 0), 0)
}
